import { templateIdForServer } from '@electron/shared/mcp-template-gating';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { electronApis } from '@/features/electron-api';
import type { McpPermission } from '@/features/integrations/integrations.types';
import {
  useAddMcpServerMutation,
  useMcpServersQuery,
} from '@/features/settings/sub-settings/settings-local-mcp.query';
import { fullDiskAccessPermissionQueryOptions } from '@/query/permissions';

import {
  findMissingPermissions,
  permissionsBlockingConnect,
  useOnFullDiskAccessGranted,
} from './server-permissions';

import type { OneClickServer } from './one-click-server';
import type { RuntimeDownloadProgress } from '@electron/shared/runtime-download.types';
import type {
  ConnectionTestProblem,
  ConnectionTestResult,
  ServerEntryInput,
} from '@littlebird/axon-mcp';

/**
 * `preparing` covers both a real CDN download and the cheap re-verify of an
 * already-installed binary; the runtime is ready when `ensureBundled` resolves,
 * never when a progress event says so — servers that ship inside the app emit
 * no progress at all.
 */
export type OneClickConnectPhase =
  | 'preparing'
  | 'ready'
  | 'testing'
  | 'saving'
  | 'connected'
  | 'failed';

export type OneClickConnectProblem =
  | 'download_failed'
  | 'connection_failed'
  | 'timed_out'
  | 'certificate_untrusted'
  | 'permission_denied'
  | 'already_connected'
  | 'save_failed';

export function oneClickProblemCopy(
  problem: OneClickConnectProblem,
  name: string
): string {
  switch (problem) {
    case 'download_failed':
      return `Couldn’t download ${name}. Check your connection, then try again.`;
    case 'connection_failed':
      return `${name} couldn’t start on this Mac. Try again.`;
    case 'timed_out':
      return `${name} is taking longer than expected to start. Try again.`;
    case 'certificate_untrusted':
      return `Littlebird couldn’t download ${name}. Your network inspects secure connections with a certificate Littlebird doesn’t trust. Ask your IT team for help.`;
    case 'permission_denied':
      return `${name} needs one more permission before Littlebird can use it.`;
    case 'already_connected':
      return `${name} is already connected.`;
    case 'save_failed':
      return `${name} started, but Littlebird couldn’t finish saving it. Try again.`;
  }
}

export interface OneClickDownloadState {
  receivedBytes: number;
  totalBytes?: number;
}

function testFailureProblem(
  problem: ConnectionTestProblem | undefined
): OneClickConnectProblem {
  return problem === 'timed_out' || problem === 'certificate_untrusted'
    ? problem
    : 'connection_failed';
}

interface UseOneClickConnectOptions {
  server: OneClickServer;
  onConnected?: () => void;
}

export function useOneClickConnect({
  server,
  onConnected,
}: UseOneClickConnectOptions) {
  // Read `.data` rather than defaulting here: a fresh `[]` each render would
  // re-create `connect` on every render.
  const servers = useMcpServersQuery().data;
  const addMutation = useAddMcpServerMutation();
  const attemptRef = useRef(0);
  const savingRef = useRef(false);
  const cancelledRef = useRef(false);
  const [phase, setPhase] = useState<OneClickConnectPhase>('preparing');
  const [problem, setProblem] = useState<OneClickConnectProblem | null>(null);
  const [download, setDownload] = useState<OneClickDownloadState | null>(null);
  const [testResult, setTestResult] = useState<ConnectionTestResult | null>(
    null
  );
  const [missingPermissions, setMissingPermissions] = useState<
    readonly McpPermission[]
  >([]);

  const bundledServerId =
    server.transport.kind === 'bundled' ? server.transport.serverId : null;

  /**
   * Downloads are single-flighted in main and survive this dialog closing, so
   * a fresh mount re-attaches to whatever is already running rather than
   * starting over.
   */
  const prepare = useCallback(async () => {
    const attempt = ++attemptRef.current;
    setPhase('preparing');
    setProblem(null);
    setMissingPermissions([]);

    try {
      if (bundledServerId) {
        await electronApis.axonMcp.ensureBundled(bundledServerId);
      }
      if (attemptRef.current !== attempt) return;
      setPhase('ready');
    } catch {
      if (attemptRef.current !== attempt) return;
      setPhase('failed');
      setProblem('download_failed');
    }
  }, [bundledServerId]);

  useEffect(() => {
    const unsubscribe = electronApis.axonMcp.onDownloadProgress(
      (event: RuntimeDownloadProgress) => {
        if (event.serverId !== bundledServerId || event.phase === 'failed') {
          return;
        }
        const progress = {
          receivedBytes: event.receivedBytes,
          totalBytes: event.totalBytes,
        };
        // An installed binary is re-verified with the same events, so only a
        // real download opens the row
        setDownload((current) =>
          event.phase === 'downloading' || current ? progress : null
        );
      }
    );
    void prepare();
    return () => {
      unsubscribe();
      // A close mid-flight abandons this attempt's results; the download keeps
      // going in main so reopening the dialog picks it back up.
      if (!savingRef.current) attemptRef.current += 1;
    };
  }, [prepare, bundledServerId]);

  // The dialog stays mounted while it animates closed, so a test finishing
  // then, or a permission granted then, must not save the server
  const cancel = useCallback(() => {
    if (savingRef.current) return false;
    cancelledRef.current = true;
    attemptRef.current += 1;
    return true;
  }, []);

  const connect = useCallback(async () => {
    if (cancelledRef.current) return;
    if (phase !== 'ready' && phase !== 'failed') return;
    if (phase === 'failed' && problem === 'download_failed') {
      await prepare();
      return;
    }

    const attempt = ++attemptRef.current;
    const fail = (nextProblem: OneClickConnectProblem) => {
      if (attemptRef.current !== attempt) return;
      setPhase('failed');
      setProblem(nextProblem);
    };

    setPhase('testing');
    setProblem(null);
    setMissingPermissions([]);
    setTestResult(null);
    // `phase` in this closure is the value from render, so it can't tell us how
    // far we got — track it locally to classify a throw correctly.
    let reachedSaving = false;

    try {
      const result = await electronApis.axonMcp.testServer(
        server.transport,
        server.name,
        server.id
      );
      if (attemptRef.current !== attempt) return;
      setTestResult(result);
      // Checked even when the handshake succeeds: some servers start without
      // the permission and only fail once a tool reads the protected data.
      // A failed handshake is also more often a missing permission than a
      // broken server, so the permission is named before a generic failure.
      const missing = await findMissingPermissions(
        permissionsBlockingConnect(server.requiredPermissions)
      );
      if (attemptRef.current !== attempt) return;
      if (missing.length > 0) {
        setMissingPermissions(missing);
        fail('permission_denied');
        return;
      }
      if (!result.ok) {
        fail(testFailureProblem(result.problem));
        return;
      }

      if (servers?.some((entry) => templateIdForServer(entry) === server.id)) {
        fail('already_connected');
        return;
      }

      const input: ServerEntryInput = {
        name: server.name,
        description: server.description,
        template: server.id,
        transport: server.transport,
        ...(server.setup && { setup: server.setup }),
        lastConnectionTest: {
          ok: true,
          ...(result.serverName && { serverName: result.serverName }),
          toolCount: result.toolCount ?? 0,
          testedAt: new Date().toISOString(),
        },
      };

      savingRef.current = true;
      reachedSaving = true;
      setPhase('saving');
      await addMutation.mutateAsync(input);
      savingRef.current = false;
      if (attemptRef.current !== attempt) return;
    } catch {
      savingRef.current = false;
      fail(reachedSaving ? 'save_failed' : 'connection_failed');
      return;
    }

    setPhase('connected');
    onConnected?.();
  }, [addMutation, onConnected, phase, prepare, problem, servers, server]);

  // Polls while the permission is missing, so granting it in System Settings
  // retries the connection without another click.
  const { data: fullDiskAccessGranted } = useQuery(
    fullDiskAccessPermissionQueryOptions({
      enabled: problem === 'permission_denied',
    })
  );
  useOnFullDiskAccessGranted(fullDiskAccessGranted, () => {
    if (problem === 'permission_denied') void connect();
  });

  const busy =
    phase === 'preparing' || phase === 'testing' || phase === 'saving';

  return {
    phase,
    problem,
    download,
    testResult,
    missingPermissions,
    busy,
    canConnect: phase === 'ready' || phase === 'failed',
    canCancel: !savingRef.current && phase !== 'saving',
    cancel,
    connect,
  };
}

export type OneClickConnectForm = ReturnType<typeof useOneClickConnect>;
